import { createSelector } from '@reduxjs/toolkit'
import type { MonthlyTarget, TargetProgressData } from '../domain/types'

// Root state selector
const selectSalesDashboard = (state: any) => state.salesDashboard

// ===== Daily Data =====
export const selectDailyData = createSelector(selectSalesDashboard, (s) => s?.dailyData)
export const selectDailyDays = createSelector(selectDailyData, (d) => d?.days ?? [])
export const selectDailyTotals = createSelector(selectDailyData, (d) => d?.totals)
export const selectTotalTons = createSelector(selectDailyTotals, (t) => t?.totalTons ?? 0)
export const selectTotalDeliveries = createSelector(selectDailyTotals, (t) => t?.totalDeliveries ?? 0)

// ===== Comparison Data =====
export const selectComparisonData = createSelector(selectSalesDashboard, (s) => s?.comparisonData)
export const selectComparisonTimeSeries = createSelector(selectComparisonData, (c) => c?.timeSeries ?? [])
export const selectComparisonChange = createSelector(selectComparisonData, (c) => c?.change)
export const selectComparisonBreakdown = createSelector(selectComparisonData, (c) => c?.breakdown ?? [])
export const selectComparisonDirection = createSelector(
  selectComparisonChange,
  (change) => {
    if (!change) return 'flat'
    if (change.tons > 0) return 'up'
    if (change.tons < 0) return 'down'
    return 'flat'
  }
)

// ===== Target Progress (array of months) =====
export const selectTargetProgressAll = createSelector(
  selectSalesDashboard,
  (s): TargetProgressData[] => s?.targetProgress ?? []
)
// Keep legacy selectors for backward compat — aggregate across all months
export const selectTargetProgress = createSelector(selectTargetProgressAll, (arr) => arr?.[0] ?? null)
export const selectTargetProgressFactories = createSelector(selectTargetProgress, (t) => t?.factories ?? [])
export const selectTargetProgressTotals = createSelector(selectTargetProgressAll, (months) => {
  if (!months || months.length === 0) return null
  const targetTons = months.reduce((s: number, m: any) => s + m.totals.targetTons, 0)
  const actualTons = months.reduce((s: number, m: any) => s + m.totals.actualTons, 0)
  return {
    targetTons,
    actualTons,
    achievementPercent: targetTons > 0 ? Math.round((actualTons / targetTons) * 10000) / 100 : 0,
    remainingTons: Math.round(Math.max(0, targetTons - actualTons) * 100) / 100,
  }
})

// ===== Targets CRUD =====
export const selectTargets = createSelector(selectSalesDashboard, (s) => s?.targets)
export const selectTargetsList = createSelector(
  selectTargets,
  (t): MonthlyTarget[] => t?.items ?? []
)
export const selectTargetsTotal = createSelector(selectTargets, (t) => t?.total ?? 0)

// ===== Controls =====
export const selectDateRange = createSelector(selectSalesDashboard, (s) => s?.dateRange)
export const selectDatePreset = createSelector(selectDateRange, (dr) => dr?.preset)
export const selectComparisonType = createSelector(selectSalesDashboard, (s) => s?.comparisonType)
export const selectFilters = createSelector(selectSalesDashboard, (s) => s?.filters ?? {})
export const selectGroupBy = createSelector(selectSalesDashboard, (s) => s?.groupBy)

// ===== Loading =====
export const selectLoading = createSelector(selectSalesDashboard, (s) => s?.loading)
export const selectDailyLoading = createSelector(selectLoading, (l) => l?.daily ?? false)
export const selectComparisonLoading = createSelector(selectLoading, (l) => l?.comparison ?? false)
export const selectTargetProgressLoading = createSelector(selectLoading, (l) => l?.targetProgress ?? false)
export const selectTargetsLoading = createSelector(selectLoading, (l) => l?.targets ?? false)
export const selectSavingTarget = createSelector(selectLoading, (l) => l?.savingTarget ?? false)
export const selectIsLoading = createSelector(
  selectDailyLoading,
  selectComparisonLoading,
  (daily, comparison) => daily || comparison
)

// ===== Error =====
export const selectError = createSelector(selectSalesDashboard, (s) => s?.error)
export const selectErrorMessage = createSelector(selectError, (e) => e?.message)
export const selectHasError = createSelector(selectErrorMessage, (msg) => Boolean(msg))

// ===== Meta =====
export const selectLastRefreshed = createSelector(selectSalesDashboard, (s) => s?.lastRefreshed)
export const selectHasData = createSelector(selectDailyData, (d) => d !== null)
